import {
  ClientSettingsSchema,
  type ClientSettings,
  type DesktopBridge,
  type EnvironmentId,
  type PersistedSavedEnvironmentRecord,
} from "@cmmd-forge/contracts";
import * as Schema from "effect/Schema";

import {
  readBrowserClientSettings,
  readBrowserSavedEnvironmentRegistry,
  readBrowserSavedEnvironmentSecret,
  removeBrowserSavedEnvironmentSecret,
  writeBrowserClientSettings,
  writeBrowserSavedEnvironmentRegistry,
  writeBrowserSavedEnvironmentSecret,
} from "./clientPersistenceStorage";

const decodeClientSettings = Schema.decodeUnknownSync(ClientSettingsSchema);

function readDesktopBridge() {
  return typeof window === "undefined" ? undefined : window.desktopBridge;
}

function readDesktopBridgeMethod<K extends keyof DesktopBridge>(
  method: K,
): DesktopBridge[K] | null {
  const bridge = readDesktopBridge();
  const candidate = bridge?.[method];
  return typeof candidate === "function" ? (candidate.bind(bridge) as DesktopBridge[K]) : null;
}

export async function getClientSettings(): Promise<ClientSettings | null> {
  const getBridgeClientSettings = readDesktopBridgeMethod("getClientSettings");
  if (getBridgeClientSettings) {
    const settings = await getBridgeClientSettings();
    return settings ? decodeClientSettings(settings) : null;
  }
  return readBrowserClientSettings();
}

export async function setClientSettings(settings: ClientSettings): Promise<void> {
  const setBridgeClientSettings = readDesktopBridgeMethod("setClientSettings");
  if (setBridgeClientSettings) {
    return setBridgeClientSettings(settings);
  }
  writeBrowserClientSettings(settings);
}

export async function getSavedEnvironmentRegistry(): Promise<
  readonly PersistedSavedEnvironmentRecord[]
> {
  const getBridgeSavedEnvironmentRegistry = readDesktopBridgeMethod("getSavedEnvironmentRegistry");
  if (getBridgeSavedEnvironmentRegistry) {
    return getBridgeSavedEnvironmentRegistry();
  }
  return readBrowserSavedEnvironmentRegistry();
}

export async function setSavedEnvironmentRegistry(
  records: readonly PersistedSavedEnvironmentRecord[],
): Promise<void> {
  const setBridgeSavedEnvironmentRegistry = readDesktopBridgeMethod("setSavedEnvironmentRegistry");
  if (setBridgeSavedEnvironmentRegistry) {
    return setBridgeSavedEnvironmentRegistry(records);
  }
  writeBrowserSavedEnvironmentRegistry(records);
}

export async function getSavedEnvironmentSecret(
  environmentId: EnvironmentId,
): Promise<string | null> {
  const getBridgeSavedEnvironmentSecret = readDesktopBridgeMethod("getSavedEnvironmentSecret");
  if (getBridgeSavedEnvironmentSecret) {
    return getBridgeSavedEnvironmentSecret(environmentId);
  }
  return readBrowserSavedEnvironmentSecret(environmentId);
}

export async function setSavedEnvironmentSecret(
  environmentId: EnvironmentId,
  secret: string,
): Promise<boolean> {
  const setBridgeSavedEnvironmentSecret = readDesktopBridgeMethod("setSavedEnvironmentSecret");
  if (setBridgeSavedEnvironmentSecret) {
    return setBridgeSavedEnvironmentSecret(environmentId, secret);
  }
  return writeBrowserSavedEnvironmentSecret(environmentId, secret);
}

export async function removeSavedEnvironmentSecret(environmentId: EnvironmentId): Promise<void> {
  const removeBridgeSavedEnvironmentSecret = readDesktopBridgeMethod(
    "removeSavedEnvironmentSecret",
  );
  if (removeBridgeSavedEnvironmentSecret) {
    return removeBridgeSavedEnvironmentSecret(environmentId);
  }
  removeBrowserSavedEnvironmentSecret(environmentId);
}
